import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/currency';
import PropTypes from 'prop-types';
import { LoaderPulse, Text } from '@reservamos/elements';
import './CompleteHeader.scss';
import { When } from 'react-if';
import { isWebView } from 'utils/webview';
import useCurrencyConfig from 'hooks/whitelabel/useCurrencyConfig';

/**
 * This component is used to display the complete header of the payment process.
 * It displays the title, the status, the total amount and the instructions for the webview.
 * It also displays the loader when the status is pendingCompleted.
 * It also displays the subtitle when the status is not failed, not pendingCompleted and not in process.
 * It also displays the webview instructions when the webview is open.
 * It also displays the currency symbol and the total amount.
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.status
 * @param {number} props.total
 * @returns {JSX.Element}
 */
const CompleteHeader = ({ title, status, total = 0 }) => {
  const { t } = useTranslation('payment');
  const { currency, locale } = useCurrencyConfig();
  const totalFormatted = formatCurrency(total, 2, currency, locale);
  const isPendingCompleted = status === 'pendingCompleted';
  const isNotFailed = status !== 'failed';
  const isInProcess = status === 'process';

  return (
    <div className={`complete-header complete-header-${status}`}>
      <div className="complete-header-overlay">
        <div className="l-container">
          <Text size="XXL" mobileSize="XL" weight="bold" color="white">
            {title}
          </Text>
          <When condition={isPendingCompleted}>
            <LoaderPulse color="white" hasWrapper={false} />
          </When>
          <When condition={isNotFailed && !isPendingCompleted && !isInProcess}>
            <Text size="XL" mobileSize="L" color="white">
              {t('payment_subtitle', { context: status, total: totalFormatted })}
            </Text>
          </When>
          <When condition={isWebView()}>
            <div className="pt-5">
              <Text size="S" mobileSize="S" color="white">
                {t('webview_instructions')}
              </Text>
            </div>
          </When>
        </div>
      </div>
    </div>
  );
};

CompleteHeader.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['success', 'error', 'pending']).isRequired,
};

export default CompleteHeader;
